import React, { useState, useMemo, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { LayoutGrid, List, SlidersHorizontal, X } from "lucide-react";
import PageHero from "../components/PageHero";
import ProductCard from "../components/ProductCard";
import CatalogueFilters from "../components/CatalogueFilters";
import { CATEGORIES, PRODUCTS, FILTER_OPTIONS, getCategory } from "../data/catalog";

const emptyFilters = () => ({
  lumenMin: FILTER_OPTIONS.lumenMin,
  lumenMax: FILTER_OPTIONS.lumenMax,
  wattMin: FILTER_OPTIONS.wattMin,
  wattMax: FILTER_OPTIONS.wattMax,
  cct: [],
  sockets: [],
  ips: [],
  warranties: [],
  cri: [],
  features: [],
});

export default function Catalogue() {
  const { slug } = useParams();
  const activeCategory = slug ? getCategory(slug) : null;

  const [query, setQuery] = useState("");
  const [filters, setFilters] = useState(emptyFilters());
  const [view, setView] = useState("grid");
  const [sort, setSort] = useState("relevance");
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [compared, setCompared] = useState([]);

  useEffect(() => {
    setFilters(emptyFilters());
    setQuery("");
  }, [slug]);

  const products = useMemo(() => {
    let list = PRODUCTS.slice();
    if (slug) list = list.filter((p) => p.category === slug);
    if (query) {
      const q = query.toLowerCase();
      list = list.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          p.code.toLowerCase().includes(q) ||
          p.sku.toLowerCase().includes(q) ||
          p.highlights.join(" ").toLowerCase().includes(q)
      );
    }
    list = list.filter(
      (p) =>
        p.lumenValue >= filters.lumenMin &&
        p.lumenValue <= filters.lumenMax &&
        p.wattValue >= filters.wattMin &&
        p.wattValue <= filters.wattMax
    );
    if (filters.cct.length) list = list.filter((p) => filters.cct.includes(p.cctValue));
    if (filters.sockets.length) list = list.filter((p) => filters.sockets.includes(p.socket));
    if (filters.ips.length) list = list.filter((p) => filters.ips.includes(p.ipValue));
    if (filters.warranties.length) list = list.filter((p) => filters.warranties.includes(p.warrantyYears));
    if (filters.cri.length) list = list.filter((p) => filters.cri.some((c) => p.cri >= c));
    if (filters.features.length) list = list.filter((p) => filters.features.every((f) => p.features.includes(f)));

    if (sort === "lumen-desc") list.sort((a, b) => b.lumenValue - a.lumenValue);
    else if (sort === "watt-asc") list.sort((a, b) => a.wattValue - b.wattValue);
    else if (sort === "newest") list.sort((a, b) => (b.tag === "NEW" ? 1 : 0) - (a.tag === "NEW" ? 1 : 0));
    return list;
  }, [slug, query, filters, sort]);

  const counts = useMemo(() => {
    const base = slug ? PRODUCTS.filter((p) => p.category === slug) : PRODUCTS;
    const ips = {};
    const warranties = {};
    base.forEach((p) => {
      ips[p.ipValue] = (ips[p.ipValue] || 0) + 1;
      warranties[p.warrantyYears] = (warranties[p.warrantyYears] || 0) + 1;
    });
    return { ips, warranties };
  }, [slug]);

  const filterPanel = (
    <CatalogueFilters
      filters={filters}
      setFilters={setFilters}
      query={query}
      setQuery={setQuery}
      counts={counts}
      onReset={() => {
        setFilters(emptyFilters());
        setQuery("");
      }}
      totalAll={PRODUCTS.length}
    />
  );

  const totalInScope = slug ? PRODUCTS.filter((p) => p.category === slug).length : PRODUCTS.length;

  return (
    <div data-testid="catalogue-page">
      <PageHero
        eyebrow={activeCategory ? "Catalogue" : "All Products"}
        title={activeCategory ? activeCategory.name : "Full Leduro Catalogue"}
        subtitle={
          activeCategory
            ? activeCategory.description
            : "Browse our complete range of LED luminaires and electrical goods. Filter by specification, search by SKU."
        }
        crumbs={[
          { label: "Catalogue", to: "/catalogue" },
          ...(activeCategory ? [{ label: activeCategory.name }] : []),
        ]}
      />

      {/* Category chips */}
      <section className="border-b border-leduro-border bg-white" data-testid="category-chips">
        <div className="leduro-container py-5 flex items-center gap-2 overflow-x-auto">
          <Link
            to="/catalogue"
            data-testid="chip-all"
            className={`shrink-0 px-4 py-2 text-xs font-bold uppercase tracking-wider border transition-colors ${
              !slug
                ? "bg-leduro-navy text-white border-leduro-navy"
                : "border-leduro-border text-leduro-navy hover:border-leduro-orange hover:text-leduro-orange"
            }`}
          >
            All products · {PRODUCTS.length}
          </Link>
          {CATEGORIES.map((c) => {
            const isActive = slug === c.slug;
            const cnt = PRODUCTS.filter((p) => p.category === c.slug).length;
            return (
              <Link
                key={c.slug}
                to={`/catalogue/${c.slug}`}
                data-testid={`chip-${c.slug}`}
                className={`shrink-0 px-4 py-2 text-xs font-bold uppercase tracking-wider border transition-colors ${
                  isActive
                    ? "bg-leduro-navy text-white border-leduro-navy"
                    : "border-leduro-border text-leduro-navy hover:border-leduro-orange hover:text-leduro-orange"
                }`}
              >
                {c.short} · {cnt}
              </Link>
            );
          })}
        </div>
      </section>

      <section className="bg-leduro-surface py-10 md:py-14">
        <div className="leduro-container grid grid-cols-1 lg:grid-cols-12 gap-8">
          <aside className="hidden lg:block lg:col-span-3">
            <div className="sticky top-28">{filterPanel}</div>
          </aside>

          <div className="lg:col-span-9">
            {/* Toolbar */}
            <div
              data-testid="catalogue-toolbar"
              className="bg-white border border-leduro-border p-4 flex flex-wrap items-center justify-between gap-3 mb-6"
            >
              <p className="text-sm text-leduro-navy">
                Showing <strong className="font-bold">{products.length}</strong> of{" "}
                <strong className="font-bold">{totalInScope}</strong>{" "}
                {products.length === 1 ? "product" : "products"}
              </p>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setMobileFiltersOpen(true)}
                  data-testid="open-mobile-filters"
                  className="lg:hidden inline-flex items-center gap-2 border border-leduro-border px-3 py-2 text-xs uppercase font-bold tracking-wider text-leduro-navy hover:bg-leduro-navy hover:text-white"
                >
                  <SlidersHorizontal className="h-4 w-4" />
                  Filters
                </button>

                <div className="relative">
                  <select
                    value={sort}
                    onChange={(e) => setSort(e.target.value)}
                    data-testid="sort-select"
                    className="appearance-none bg-white border border-leduro-border pl-3 pr-9 py-2 text-sm text-leduro-navy focus:outline-none focus:border-leduro-orange cursor-pointer"
                  >
                    <option value="relevance">Relevance</option>
                    <option value="newest">Newest</option>
                    <option value="lumen-desc">Lumens (high → low)</option>
                    <option value="watt-asc">Wattage (low → high)</option>
                  </select>
                  <svg className="absolute right-2 top-1/2 -translate-y-1/2 h-3 w-3 text-leduro-muted pointer-events-none" viewBox="0 0 12 12" fill="none">
                    <path d="M3 5l3 3 3-3" stroke="currentColor" strokeWidth="1.5" />
                  </svg>
                </div>

                <div className="flex border border-leduro-border">
                  <button
                    onClick={() => setView("grid")}
                    data-testid="view-grid"
                    aria-label="Grid view"
                    className={`h-10 w-10 grid place-items-center transition-colors ${
                      view === "grid" ? "bg-leduro-navy text-white" : "text-leduro-navy hover:bg-leduro-surface"
                    }`}
                  >
                    <LayoutGrid className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setView("list")}
                    data-testid="view-list"
                    aria-label="List view"
                    className={`h-10 w-10 grid place-items-center border-l border-leduro-border transition-colors ${
                      view === "list" ? "bg-leduro-navy text-white" : "text-leduro-navy hover:bg-leduro-surface"
                    }`}
                  >
                    <List className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Active filter chips */}
            {(filters.cct.length || filters.sockets.length || filters.ips.length || filters.warranties.length || filters.cri.length || filters.features.length || query) && (
              <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="active-filters">
                <span className="text-[11px] uppercase font-bold tracking-wider text-leduro-muted">Active:</span>
                {query && (
                  <ActiveChip label={`"${query}"`} onClose={() => setQuery("")} testId="active-search" />
                )}
                {filters.cct.map((v) => (
                  <ActiveChip key={`c${v}`} label={`${v}K`} onClose={() => setFilters({ ...filters, cct: filters.cct.filter((x) => x !== v) })} testId={`active-cct-${v}`} />
                ))}
                {filters.sockets.map((v) => (
                  <ActiveChip key={`s${v}`} label={v} onClose={() => setFilters({ ...filters, sockets: filters.sockets.filter((x) => x !== v) })} testId={`active-socket-${v}`} />
                ))}
                {filters.ips.map((v) => (
                  <ActiveChip key={`ip${v}`} label={`IP${v}`} onClose={() => setFilters({ ...filters, ips: filters.ips.filter((x) => x !== v) })} testId={`active-ip-${v}`} />
                ))}
                {filters.warranties.map((v) => (
                  <ActiveChip key={`w${v}`} label={`${v}Y warranty`} onClose={() => setFilters({ ...filters, warranties: filters.warranties.filter((x) => x !== v) })} testId={`active-warranty-${v}`} />
                ))}
                {filters.cri.map((v) => (
                  <ActiveChip key={`cri${v}`} label={`Ra ${v}+`} onClose={() => setFilters({ ...filters, cri: filters.cri.filter((x) => x !== v) })} testId={`active-cri-${v}`} />
                ))}
                {filters.features.map((f) => (
                  <ActiveChip key={`f${f}`} label={f.replace(/-/g, " ")} onClose={() => setFilters({ ...filters, features: filters.features.filter((x) => x !== f) })} testId={`active-feature-${f}`} />
                ))}
              </div>
            )}

            {products.length === 0 ? (
              <div
                data-testid="catalogue-empty"
                className="border border-dashed border-leduro-border bg-white p-16 text-center"
              >
                <p className="font-heading font-bold text-leduro-navy text-xl">No products match your filters.</p>
                <p className="text-sm text-leduro-muted mt-2">Try clearing some filters or a different SKU.</p>
                <button onClick={() => { setFilters(emptyFilters()); setQuery(""); }} data-testid="empty-reset" className="leduro-btn-ghost mt-6">
                  Reset all filters
                </button>
              </div>
            ) : view === "grid" ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                {products.map((p) => (
                  <ProductCard
                    key={p.id}
                    product={p}
                    viewMode="grid"
                    compared={compared.includes(p.id)}
                    onCompare={(id) =>
                      setCompared((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id]))
                    }
                  />
                ))}
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                {products.map((p) => (
                  <ProductCard
                    key={p.id}
                    product={p}
                    viewMode="list"
                    compared={compared.includes(p.id)}
                    onCompare={(id) =>
                      setCompared((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id]))
                    }
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Mobile filter drawer */}
      {mobileFiltersOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" data-testid="mobile-filter-drawer">
          <div className="absolute inset-0 bg-leduro-navy/60" onClick={() => setMobileFiltersOpen(false)} />
          <div className="absolute right-0 top-0 bottom-0 w-full max-w-sm bg-white overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-leduro-border flex items-center justify-between p-4 z-10">
              <span className="font-heading font-bold text-leduro-navy">Filters</span>
              <button onClick={() => setMobileFiltersOpen(false)} data-testid="close-mobile-filters" className="h-9 w-9 grid place-items-center border border-leduro-border">
                <X className="h-4 w-4 text-leduro-navy" />
              </button>
            </div>
            <div className="p-4">{filterPanel}</div>
          </div>
        </div>
      )}

      {/* Compare floating bar */}
      {compared.length > 0 && (
        <div
          data-testid="compare-bar"
          className="fixed bottom-0 left-0 right-0 z-40 bg-leduro-navy text-white border-t-4 border-leduro-orange shadow-[0_-12px_30px_-12px_rgba(0,0,0,0.25)]"
        >
          <div className="leduro-container flex items-center justify-between gap-4 py-3">
            <span className="text-sm">
              <strong className="font-bold">{compared.length}</strong> product{compared.length !== 1 ? "s" : ""} selected to compare
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCompared([])}
                data-testid="compare-clear"
                className="text-xs uppercase tracking-wider font-bold text-white/70 hover:text-leduro-orange"
              >
                Clear
              </button>
              <button data-testid="compare-go" className="leduro-btn-primary !py-2 text-xs">
                Compare →
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const ActiveChip = ({ label, onClose, testId }) => (
  <span
    data-testid={testId}
    className="inline-flex items-center gap-1.5 bg-leduro-navy text-white text-xs font-mono uppercase tracking-wider px-2.5 py-1.5"
  >
    {label}
    <button onClick={onClose} className="hover:text-leduro-orange" aria-label="Remove filter">
      <X className="h-3 w-3" />
    </button>
  </span>
);
